import { MARCAS_DE_CAMPANHA } from '@titularis/analytics';

/**
 * O funil, num lugar só.
 *
 * A landing não autentica ninguém e não cobra de ninguém: ela entrega o
 * visitante no app, que é quem tem sessão e checkout. O que ela faz de
 * importante é **não perder a intenção no caminho** - quem clicou em comprar
 * chega no app já apontado para os planos, e volta para lá depois de entrar
 * (ver `pages/auth/destino.ts`, do lado de lá).
 *
 * Três portas, e é de propósito que sejam três:
 *
 * - **Criar conta** é a porta larga, para quem chegou de um link de grupo e
 *   ainda não sabe o que é isto. A cota gratuita semanal existe justamente para
 *   essa pessoa provar antes de decidir;
 * - **Entrar** é para quem já é aluno e clicou no cabeçalho;
 * - **Comprar** é a porta estreita, de quem já leu a página e decidiu. Ela
 *   passa pelo cadastro do mesmo jeito - não dá para vender sem conta -, mas
 *   sai do outro lado nos planos, e não na vitrine de módulos.
 */
/** A base do app. Exportada porque o ouvinte de CTA precisa saber o que é saída. */
export const APP = import.meta.env.VITE_APP_URL ?? 'https://app.titularis.com.br';

/**
 * As marcas de campanha que esta visita trouxe, para irem junto ao app.
 *
 * **A travessia não pode depender só do cookie.** A atribuição viaja num cookie
 * de `.titularis.com.br`, e ele funciona - há inscrição gravada com o primeiro
 * toque de dois dias antes. Onde ele falha é exatamente onde o tráfego de grupo
 * e de Instagram chega: navegador embutido de aplicativo, armazenamento
 * particionado, aba anônima. Nesses casos a conta nascia como tráfego direto.
 *
 * Levando as marcas no endereço, o app as captura por conta própria - e o
 * PostHog dele, que só conhece a URL que vê, passa a ter `$initial_utm_source`
 * em vez de um canal `Unknown`.
 *
 * Pura e exportada porque é a única decisão deste arquivo, e erra calado: de
 * menos, a atribuição continua dependendo do cookie; de mais, um parâmetro
 * qualquer da landing viraria campanha no relatório do app.
 */
export function marcasDaVisita(busca: string): string {
  const veio = new URLSearchParams(busca);
  const levar = new URLSearchParams();

  for (const marca of MARCAS_DE_CAMPANHA) {
    const valor = veio.get(marca)?.trim();
    if (valor) levar.set(marca, valor);
  }

  return levar.toString();
}

/*
 * Lida uma vez, na carga do módulo, que é quando a busca ainda é a da chegada:
 * quem entra por `/?utm_source=whatsapp` e navega para `/provas` perde a query
 * na barra de endereço, e os CTAs de lá continuam levando a campanha - que é o
 * comportamento certo, porque a visita é a mesma.
 */
const MARCAS = typeof globalThis.location === 'undefined'
  ? ''
  : marcasDaVisita(globalThis.location.search);

/** Caminho no app, carregando o destino de volta depois do login. */
function comDestino(caminho: string, destino?: string): string {
  const busca = new URLSearchParams(MARCAS);
  if (destino) busca.set('destino', destino);

  const query = busca.toString();
  return query ? `${APP}${caminho}?${query}` : `${APP}${caminho}`;
}

export const destinos = {
  entrar: comDestino('/entrar'),
  criarConta: comDestino('/criar-conta'),
  /**
   * Cadastro com a compra do outro lado.
   *
   * Manda para o cadastro, e não para os planos: quem ainda não tem conta seria
   * jogado no login pelo `RequireAuth` de qualquer forma, e chegar direto ao
   * formulário certo economiza um salto de quem já decidiu comprar.
   *
   * O destino é `/configuracoes/plano`, onde a tela mora desde 05/09/2026. Os
   * endereços anteriores ainda redirecionam, mas mandar para eles custaria um
   * salto a mais na volta do login - e é justamente o momento em que a pessoa
   * está esperando a tela de compra aparecer.
   */
  comprar: comDestino('/criar-conta', '/configuracoes/plano'),
  /** Para quem já tem conta e só quer ver a tabela de preços por dentro. */
  planos: comDestino('/entrar', '/configuracoes/plano'),
  /**
   * Cadastro com a 1ª fase do outro lado (15/10/2026).
   *
   * A porta larga, como a do Enac, mas aterrissando nas questões da 1ª fase e
   * não na tela de início: quem clicou no cartão dela quer ver as provas
   * estaduais, e procurá-las na barra depois do cadastro é o salto que a
   * intenção não sobrevive.
   */
  primeiraFase: comDestino('/criar-conta', '/estudar?module=objective'),
} as const;
